/**
 * ScopedModalView - Modal scoped to player detail content area
 *
 * On web: renders via portal within the player page content area.
 * On native: renders via RN Modal for reliable full-screen overlay.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { XIcon } from "lucide-react-native";
import React, { ReactNode, memo } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { NativeModal } from "@/components/atoms/NativeModal";
import { Pressable } from "@/components/atoms/Pressable";
import { GorhomPortal, PLAYER_DETAIL_PORTAL } from "@/components/atoms/Portal";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";
import { RADIUS } from "@/config/radius";

/**
 * Props for ScopedModalView component
 */
interface ScopedModalViewProps {
  /** Whether the modal is visible */
  readonly isOpen: boolean;
  /** Callback when modal should close */
  readonly onClose: () => void;
  /** Modal title displayed in header */
  readonly title: string;
  /** Modal content */
  readonly children: ReactNode;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Optional max width in pixels (default: 600) */
  readonly maxWidth?: number;
  /** Optional max height as percentage (default: 80%) */
  readonly maxHeightPercent?: number;
  /** Optional header content to render next to close button */
  readonly headerContent?: ReactNode;
}

/**
 * Stops event propagation to prevent backdrop close when pressing modal content
 * @param e - Press event object
 * @param e.stopPropagation - Function to stop event bubbling
 */
const handleStopPropagation = (e: { stopPropagation: () => void }) => {
  e.stopPropagation();
};

/**
 * Shared modal inner content (backdrop + card) used by both renderers
 * @param root0 - Component props
 * @param root0.onClose - Callback invoked when modal should close
 * @param root0.title - Modal title displayed in header
 * @param root0.children - Modal body content
 * @param root0.isDark - Whether dark theme is active
 * @param root0.maxWidth - Max width in pixels for the modal card
 * @param root0.maxHeightPercent - Max height as percentage of screen
 * @param root0.headerContent - Optional content next to close button
 */
const ScopedModalInnerContent = ({
  onClose,
  title,
  children,
  isDark,
  maxWidth = 600,
  maxHeightPercent = 80,
  headerContent,
}: Omit<ScopedModalViewProps, "isOpen">) => (
  <Pressable
    className="flex-1 items-center justify-center"
    // eslint-disable-next-line no-restricted-syntax -- dimmed scrim backdrop rgba(0,0,0,0.5); no semantic scrim token and arbitrary opacity is banned
    UNSAFE_style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    onPress={onClose}
  >
    {/* Modal container — full screen on native, card on web */}
    <Pressable
      onPress={handleStopPropagation}
      // eslint-disable-next-line no-restricted-syntax -- platform-forked, prop/percentage/theme-driven modal card geometry (maxWidth prop, `${maxHeightPercent}%`, %-based widths, isDark hex border/bg); off the closed token ladder
      UNSAFE_style={
        Platform.OS === "web"
          ? {
              width: "90%",
              maxWidth,
              maxHeight: `${maxHeightPercent}%`,
              borderRadius: RADIUS.lg,
              overflow: "hidden",
              borderWidth: 1,
              borderColor: isDark ? "#27272a" : "#e4e4e7",
              backgroundColor: isDark ? "#18181b" : "#ffffff",
            }
          : {
              width: "100%",
              height: "100%",
              paddingTop: 56,
              backgroundColor: isDark ? "#18181b" : "#ffffff",
            }
      }
    >
      {/* Header */}
      <Box className="flex-row items-center justify-between border-b border-outline-subtle p-4">
        <Stack direction="horizontal" className="flex-1 items-center gap-2">
          <Text
            variant="title"
            className={isDark ? "text-white" : "text-content-primary"}
          >
            {title}
          </Text>
        </Stack>
        <Stack direction="horizontal" className="items-center gap-2">
          {headerContent}
          <Pressable onPress={onClose}>
            <Box className="rounded-full bg-surface-action p-2">
              <Icon as={XIcon} size="md" className="text-content-subtle" />
            </Box>
          </Pressable>
        </Stack>
      </Box>

      {/* Body */}
      <ScrollView
        className="bg-surface-base p-4"
        showsVerticalScrollIndicator
        contentContainerStyle={{ paddingBottom: 16 }}
      >
        {children}
      </ScrollView>
    </Pressable>
  </Pressable>
);

/**
 * Modal component scoped to player detail content area via portal (web)
 * or RN Modal (native) for reliable full-screen rendering
 * @param props - Component props
 * @param props.isOpen - Whether modal is visible
 * @param props.onClose - Close handler
 * @param props.title - Modal title
 * @param props.children - Modal content
 * @param props.isDark - Whether dark mode is active
 * @param props.maxWidth - Max width in pixels
 * @param props.maxHeightPercent - Max height as percentage
 * @param props.headerContent - Optional header content
 */
const ScopedModalView: React.FC<ScopedModalViewProps> =
  Platform.OS === "web"
    ? ({
        isOpen,
        onClose,
        title,
        children,
        isDark,
        maxWidth,
        maxHeightPercent,
        headerContent,
      }) =>
        isOpen ? (
          <GorhomPortal name={PLAYER_DETAIL_PORTAL}>
            <Pressable className="absolute inset-0" onPress={onClose}>
              <ScopedModalInnerContent
                onClose={onClose}
                title={title}
                isDark={isDark}
                maxWidth={maxWidth}
                maxHeightPercent={maxHeightPercent}
                headerContent={headerContent}
              >
                {children}
              </ScopedModalInnerContent>
            </Pressable>
          </GorhomPortal>
        ) : null
    : ({
        isOpen,
        onClose,
        title,
        children,
        isDark,
        maxWidth,
        maxHeightPercent,
        headerContent,
      }) =>
        isOpen ? (
          <NativeModal
            visible={isOpen}
            transparent
            animationType="fade"
            onRequestClose={onClose}
          >
            <ScopedModalInnerContent
              onClose={onClose}
              title={title}
              isDark={isDark}
              maxWidth={maxWidth}
              maxHeightPercent={maxHeightPercent}
              headerContent={headerContent}
            >
              {children}
            </ScopedModalInnerContent>
          </NativeModal>
        ) : null;

ScopedModalView.displayName = "ScopedModalView";

export default memo(ScopedModalView);
